import React, { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import useModuleAccess from '../hooks/useModuleAccess'

const API_URL = process.env.REACT_APP_API_URL

const ProcurementView = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { canUpdate } = useModuleAccess('procurement_tracker')
  const [procurements, setProcurements] = useState([])
  const [commonData, setCommonData] = useState({
    prNo: '',
    clientReference: '',
    deliveryLocation: '',
    dueDate: '',
    prSummary: ''
  })
  const [isLoading, setIsLoading] = useState(true)
  const [downloadFilesById, setDownloadFilesById] = useState({})
  const [isLoadingDownloadsById, setIsLoadingDownloadsById] = useState({})

  useEffect(() => {
    fetchProcurement()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Format date to IST with AM/PM (DD/MM/YYYY)
  const formatDateToIST = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleString('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    })
  }

  // Format date without time (DD/MM/YYYY). Supports ISO and API DD-MM-YYYY / DD/MM/YYYY.
  const formatDate = (dateString) => {
    if (!dateString) return '-'
    const str = String(dateString).trim()
    let date = null

    if (/^\d{4}-\d{2}-\d{2}/.test(str)) {
      date = new Date(str)
    } else {
      const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/)
      if (dmy) {
        date = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]))
      } else {
        date = new Date(str)
      }
    }

    if (!date || Number.isNaN(date.getTime())) return '-'
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
  }

  // Get quotation status color class
  const getQuotationStatusColor = (status) => {
    if (!status) return 'f_orange'
    const statusLower = status.toLowerCase()

    switch (statusLower) {
      case 'submitted':
        return 'f_green'
      case 'pending':
        return 'f_orange'
      case 'regret':
        return 'f_red'
      case 'hold':
        return 'text-warning'
      default:
        return 'f_orange'
    }
  }

  const fetchDownloadFilesForProcurementId = async (procurementId) => {
    const payload = {
      action: 'get-procurement-download-presigned-url',
      id: procurementId
    }

    const response = await axios.post(API_URL, payload)
    let responseData = response.data
    if (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData)
      } catch (e) {
        responseData = response.data
      }
    }
    if (responseData && responseData.body) {
      if (typeof responseData.body === 'string') {
        try {
          responseData = JSON.parse(responseData.body)
        } catch (e) {
          responseData = responseData.body
        }
      } else {
        responseData = responseData.body
      }
    }

    if (responseData?.success === true && Array.isArray(responseData.files)) {
      return responseData.files
    }

    throw new Error(responseData?.message || 'Failed to load attachments')
  }

  const handleOpenAttachment = async (procurementId, fileIndex) => {
    try {
      const existing = downloadFilesById[procurementId]
      if (Array.isArray(existing) && existing[fileIndex]?.downloadUrl) {
        window.open(existing[fileIndex].downloadUrl, '_blank', 'noopener,noreferrer')
        return
      }

      setIsLoadingDownloadsById((prev) => ({ ...prev, [procurementId]: true }))
      const files = await fetchDownloadFilesForProcurementId(procurementId)
      setDownloadFilesById((prev) => ({ ...prev, [procurementId]: files }))

      if (files[fileIndex]?.downloadUrl) {
        window.open(files[fileIndex].downloadUrl, '_blank', 'noopener,noreferrer')
      } else {
        toast.error('File link not available', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Open attachment error:', error)
      toast.error('Failed to open attachment. Please try again.', { autoClose: 2000 })
    } finally {
      setIsLoadingDownloadsById((prev) => ({ ...prev, [procurementId]: false }))
    }
  }

  const fetchProcurement = async () => {
    if (!id) {
      toast.error("Procurement ID is missing", { autoClose: 2000 })
      navigate('/ProcurementTracker')
      return
    }

    setIsLoading(true)
    try {
      // First, get the single procurement to get the PR number
      const payload = {
        action: "get-procurement-by-id",
        id: id
      }

      const response = await axios.post(API_URL, payload)
      let responseData = response.data

      if (typeof responseData === 'string') {
        try {
          responseData = JSON.parse(responseData)
        } catch (parseError) {
          responseData = response.data
        }
      }

      if (responseData.success === true && responseData.data) {
        const prNo = responseData.data.prNo
        
        // Now fetch all procurements with the same PR number
        const allPayload = {
          action: 'get-procurements-by-prno',
          prNo: prNo
        }

        const allResponse = await axios.post(API_URL, allPayload)
        let allResponseData = allResponse.data

        if (typeof allResponseData === 'string') {
          try {
            allResponseData = JSON.parse(allResponseData)
          } catch (parseError) {
            allResponseData = allResponse.data
          }
        }

        if (allResponseData.success === true && allResponseData.data && allResponseData.data.length > 0) {
          setProcurements(allResponseData.data)
          
          // Set common data from first record
          const firstRecord = allResponseData.data[0]
          setCommonData({
            prNo: firstRecord.prNo || '',
            clientReference: firstRecord.clientReference || '',
            deliveryLocation: firstRecord.deliveryLocation || '',
            dueDate: firstRecord.dueDate || responseData.data.dueDate || '',
            prSummary: firstRecord.prSummary || responseData.data.prSummary || ''
          })
        } else {
          toast.error("Failed to fetch procurement details", { autoClose: 2000 })
          navigate('/ProcurementTracker')
        }
      } else {
        toast.error(responseData.message || "Failed to fetch procurement details", { autoClose: 2000 })
        navigate('/ProcurementTracker')
      }
    } catch (error) {
      console.error("Fetch procurement error:", error)
      if (error.response && error.response.data) {
        let errorData = error.response.data
        if (typeof errorData === 'string') {
          try {
            errorData = JSON.parse(errorData)
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(errorData.message || "An error occurred while fetching procurement details", { autoClose: 2000 })
      } else {
        toast.error("Network error. Please check your connection and try again.", { autoClose: 2000 })
      }
      navigate('/ProcurementTracker')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Purchase Request</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/ProcurementTracker"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!procurements || procurements.length === 0) {
    return (
      <div>
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2 align-items-center">
              <div className="col-6 col-sm-6">
                <h1 className="m-0 txt_lft">View Purchase Request</h1>
              </div>
              <div className="col-6 col-sm-6">
                <ol className="breadcrumb justify-content-end">
                  <li className="breadcrumb-item">
                    <Link to="/ProcurementTracker"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body text-center py-5">
                    <p>Procurement entries not found</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 align-items-center">
            <div className="col-6 col-sm-6">
              <h1 className="m-0 txt_lft">View Purchase Request</h1>
            </div>
            <div className="col-6 col-sm-6">
              <ol className="breadcrumb justify-content-end">
                <li className="breadcrumb-item">
                  <Link to="/ProcurementTracker"><i className="fa-solid fa-arrow-left-long" /> Back</Link>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="card card-primary card-outline">
                <div className="card-body">
                  {/* Procurement Entry Form Header */}
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                    <h5 className="mb-0">
                      <i className="fa-solid fa-table mr-2"></i>
                      Purchase Entry Form
                    </h5>
                  </div>

                  {/* Common Fields - Horizontal Layout */}
                  <div className="row mb-4">
                    <div className="col-6 col-md-2 col-lg-2">
                      <div className="form-group mb-md-0">
                        <label style={{ fontWeight: '600', fontSize: '13px' }}>PR NUMBER</label>
                        <div
                          className="txtdsply d-inline-block"
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            width: 'auto',
                            maxWidth: '100%'
                          }}
                        >
                          <span className="badge badge-info" style={{ fontSize: '95%', padding: '4px 8px' }}>
                            {commonData.prNo || '-'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group mb-md-0">
                        <label style={{ fontWeight: '600', fontSize: '13px' }}>CLIENT REFERENCE</label>
                        <div className="txtdsply" style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          {commonData.clientReference || '-'}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="form-group mb-md-0">
                        <label style={{ fontWeight: '600', fontSize: '13px' }}>LOCATION</label>
                        <div className="txtdsply" style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          {commonData.deliveryLocation || '-'}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-2">
                      <div className="form-group mb-md-0">
                        <label style={{ fontWeight: '600', fontSize: '13px' }}>PR DUE DATE</label>
                        <div className="txtdsply" style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          {formatDate(commonData.dueDate)}
                        </div>
                      </div>
                    </div>
                    <div className="col-md-3">
                      <div className="form-group mb-md-0">
                        <label style={{ fontWeight: '600', fontSize: '13px' }}>PR SUMMARY</label>
                        <div
                          className="txtdsply"
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4px',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                          }}
                        >
                          {commonData.prSummary || '-'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Horizontal Table for Multiple Rows with All Details */}
                  <div className="table-responsive" style={{ position: 'relative' }}>
                    <table className="table table-bordered table-hover" style={{ fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#17324d', color: '#fff' }}>
                        <tr>
                          <th style={{ minWidth: '60px', position: 'sticky', left: 0, backgroundColor: '#17324d', zIndex: 10, borderRight: '2px solid #dee2e6' }}>S.NO</th>
                          <th style={{ minWidth: '100px', position: 'sticky', left:  '60px', backgroundColor: '#17324d', zIndex: 10, borderRight: '2px solid #dee2e6' }}>Material Code</th>
                          <th style={{ minWidth: '200px', position: 'sticky', left: '160px', backgroundColor: '#17324d', zIndex: 10, borderRight: '2px solid #dee2e6' }}>Material Description</th>
                          <th style={{ minWidth: '100px' }}>HSN</th>
                          <th style={{ minWidth: '80px' }}>UOM</th>
                          <th style={{ minWidth: '80px' }}>Quantity</th>
                          <th style={{ minWidth: '100px' }}>Basic Price</th>
                          <th style={{ minWidth: '100px' }}>GST</th>
                          <th style={{ minWidth: '100px' }}>Total Value</th>
                          <th style={{ minWidth: '100px' }}>Quote Price</th>
                          <th style={{ minWidth: '120px' }}>Quotation Status</th>
                          <th style={{ minWidth: '150px' }}>Quote Reference</th>
                          <th style={{ minWidth: '150px' }}>Supplier Reference</th>
                          <th style={{ minWidth: '200px' }}>Purchase Remarks</th>
                          <th style={{ minWidth: '200px' }}>Sales Remarks</th>
                          <th style={{ minWidth: '120px' }}>Attachments</th>
                           <th style={{ minWidth: '150px' }}>Assigned User</th>
                          <th style={{ minWidth: '120px' }}>User Email</th>
                          <th style={{ minWidth: '120px' }}>User Phone</th>
                          <th style={{ minWidth: '150px' }}>Created By</th>
                          <th style={{ minWidth: '150px' }}>Created On</th>
                          <th style={{ minWidth: '150px' }}>Updated By</th>
                          <th style={{ minWidth: '150px' }}>Updated On</th>
                          {canUpdate && (
                            <th style={{ minWidth: '100px', textAlign: 'center' }}>Action</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {procurements.flatMap((procurement, index) => {
                          const attachmentCount = Array.isArray(procurement.attachments)
                            ? procurement.attachments.length
                            : 0
                          const procurementId = procurement._id
                          const isLoadingDownloads = Boolean(
                            procurementId && isLoadingDownloadsById[procurementId]
                          )
                          const lineNo = procurement.lineNo || index + 1
                          const stickyBg = '#fff'
                          const parentRow = (
                          <tr key={procurement._id || index}>
                            <td style={{ position: 'sticky', left: 0, backgroundColor: stickyBg, zIndex: 5, borderRight: '2px solid #dee2e6', fontWeight: 600 }}>
                              <div className="txtdsply">
                                {lineNo}
                                {procurement.isBoq ? (
                                  <span className="badge badge-warning ml-1" style={{ fontSize: '9px' }}>
                                    BOQ
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td style={{ position: 'sticky', left:  '60px', backgroundColor: stickyBg, zIndex: 5, borderRight: '2px solid #dee2e6' }}>
                              <div className="txtdsply">{procurement.materialCode || '-'}</div>
                            </td>
                            <td style={{ position: 'sticky', left: '160px', backgroundColor: stickyBg, zIndex: 5, borderRight: '2px solid #dee2e6' }}>
                              <div className="txtdsply" style={{ whiteSpace: 'pre-wrap', maxWidth: '300px' }}>
                                {procurement.materialDescription || '-'}
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">{procurement.hsnCode || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">{procurement.uom || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">
                                <span className="badge badge-secondary" style={{ fontSize: '90%' }}>
                                  {procurement.quantity || 0}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">₹ {procurement.basicPrice ? procurement.basicPrice.toFixed(2) : '0.00'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">₹ {procurement.gst ? procurement.gst.toFixed(2) : '0.00'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">
                                <strong>₹ {procurement.totalValue ? procurement.totalValue.toFixed(2) : '0.00'}</strong>
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">₹ {procurement.quotePrice ? procurement.quotePrice.toFixed(2) : '0.00'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">
                                <span className={`fwt_600 ${getQuotationStatusColor(procurement.quotationStatus)}`}>
                                  {procurement.quotationStatus || 'Pending'}
                                </span>
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">{procurement.quoteReference || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply" style={{ whiteSpace: 'pre-wrap', maxWidth: '250px' }}>
                                {Array.isArray(procurement.supplierReference)
                                  ? procurement.supplierReference.filter(Boolean).join(', ')
                                  : procurement.supplierReference || '-'}
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply" style={{ whiteSpace: 'pre-wrap', maxWidth: '250px' }}>
                                {procurement.procurementRemarks || '-'}
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply" style={{ whiteSpace: 'pre-wrap', maxWidth: '250px' }}>
                                {procurement.technicalQueries || '-'}
                              </div>
                            </td>
                            <td style={{ fontSize: '12px' }}>
                              {attachmentCount > 0 && procurementId ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {Array.from({ length: attachmentCount }).map((_, fileIdx) => (
                                    <button
                                      key={`${procurementId}-file-${fileIdx}`}
                                      type="button"
                                      className="btn btn-link p-0"
                                      style={{ fontSize: '12px' }}
                                      onClick={() => handleOpenAttachment(procurementId, fileIdx)}
                                      disabled={isLoadingDownloads}
                                      title="Open attachment"
                                    >
                                      File {fileIdx + 1}
                                    </button>
                                  ))}
                                  {isLoadingDownloads && (
                                    <span className="text-muted" style={{ fontSize: '12px' }}>
                                      Loading...
                                    </span>
                                  )}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                             <td>
                              <div className="txtdsply">{procurement.userId?.name || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">{procurement.userId?.email || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">{procurement.userId?.phoneNumber || '-'}</div>
                            </td>
                            <td>
                              <div className="txtdsply">
                                {procurement.createdBy?.name || '-'}
                                {procurement.createdBy?.email && (
                                  <small className="text-muted d-block">{procurement.createdBy.email}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">{formatDateToIST(procurement.createdAt)}</div>
                            </td>
                            <td>
                              <div className="txtdsply">
                                {procurement.updatedBy?.name || '-'}
                                {procurement.updatedBy?.email && (
                                  <small className="text-muted d-block">{procurement.updatedBy.email}</small>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="txtdsply">{formatDateToIST(procurement.updatedAt)}</div>
                            </td>
                            {canUpdate && (
                              <td style={{ textAlign: 'center' }}>
                                <div className="d-flex justify-content-center align-items-center">
                                  <Link to={`/procurementTrackerEdit/${procurement._id}`} className="crsr_pntr" title="Edit">
                                    <i className="fa-solid fa-pen" />
                                  </Link>
                                </div>
                              </td>
                            )}
                          </tr>
                          )

                          const boqItems =
                            procurement.isBoq && Array.isArray(procurement.boq)
                              ? procurement.boq
                              : []

                          const boqRows = boqItems.map((boqItem, boqIndex) => {
                            const boqNo =
                              boqItem.boqNo || `${lineNo}.${boqIndex + 1}`
                            const boqBg = '#fff8e6'
                            return (
                              <tr
                                key={`${procurement._id || index}-boq-${boqItem._id || boqIndex}`}
                                style={{ backgroundColor: boqBg }}
                              >
                                <td
                                  style={{
                                    position: 'sticky',
                                    left: 0,
                                    backgroundColor: boqBg,
                                    zIndex: 5,
                                    borderRight: '2px solid #dee2e6',
                                    fontWeight: 600,
                                    color: '#856404',
                                    paddingLeft: '14px'
                                  }}
                                >
                                  <div className="txtdsply">{boqNo}</div>
                                </td>
                                <td
                                  style={{
                                    position: 'sticky',
                                    left: '60px',
                                    backgroundColor: boqBg,
                                    zIndex: 5,
                                    borderRight: '2px solid #dee2e6'
                                  }}
                                >
                                  <div className="txtdsply">
                                    {boqItem.materialCode || '-'}
                                  </div>
                                </td>
                                <td
                                  style={{
                                    position: 'sticky',
                                    left: '160px',
                                    backgroundColor: boqBg,
                                    zIndex: 5,
                                    borderRight: '2px solid #dee2e6'
                                  }}
                                >
                                  <div
                                    className="txtdsply"
                                    style={{
                                      whiteSpace: 'pre-wrap',
                                      maxWidth: '300px'
                                    }}
                                  >
                                    {boqItem.materialDescription || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="txtdsply">
                                    {boqItem.hsnCode || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="txtdsply">
                                    {boqItem.uom || '-'}
                                  </div>
                                </td>
                                <td>
                                  <div className="txtdsply">
                                    <span
                                      className="badge badge-warning"
                                      style={{ fontSize: '90%' }}
                                    >
                                      {boqItem.quantity || 0}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                <td>
                                  <div className="txtdsply text-muted">—</div>
                                </td>
                                {canUpdate && (
                                  <td style={{ textAlign: 'center' }}>
                                    <div className="txtdsply text-muted">—</div>
                                  </td>
                                )}
                              </tr>
                            )
                          })

                          return [parentRow, ...boqRows]
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>               
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ProcurementView
